import type { Role } from './types/user';

export type Action =
  | 'read' | 'create' | 'update' | 'delete' | 'cancel' | 'assign' | 'resolve' | 'close' | 'link'
  | 'approve' | 'publish' | 'dispatch' | 'run' | 'manage';

export type Resource =
  | 'incident' | 'problem' | 'change' | 'ci' | 'asset' | 'article' | 'catalog' | 'report'
  | 'company' | 'contact' | 'case' | 'contract' | 'workOrder' | 'slaPolicy' | 'assignmentRule'
  | 'agent' | 'agentRun' | 'user' | 'group' | 'settings' | 'auditLog' | 'notification';

export interface PermissionContext {
  uid?: string;
  /** Owner of the record being acted on (creator or assignee, as the caller decides). */
  ownerId?: string | null;
}

interface Grant {
  roles: Role[];
  actions: Action[];
  ownOnly?: boolean;
}

const STAFF: Role[] = ['admin', 'tech'];
const ADMIN: Role[] = ['admin'];
const TECH: Role[] = ['tech'];

const workRecord: Grant[] = [
  { roles: STAFF, actions: ['read', 'create', 'update', 'assign', 'resolve', 'close', 'link'] },
  { roles: ADMIN, actions: ['delete', 'cancel'] },
  { roles: TECH, actions: ['cancel'], ownOnly: true },
];
const configRecord: Grant[] = [
  { roles: STAFF, actions: ['read', 'create', 'update'] },
  { roles: ADMIN, actions: ['delete'] },
];
const adminManaged: Grant[] = [
  { roles: STAFF, actions: ['read'] },
  { roles: ADMIN, actions: ['create', 'update', 'delete', 'manage'] },
];

const MATRIX: Record<Resource, Grant[]> = {
  incident: workRecord,
  problem: workRecord,
  case: workRecord,
  change: [
    { roles: STAFF, actions: ['read', 'create', 'update', 'assign', 'link'] },
    { roles: ADMIN, actions: ['approve', 'close', 'delete', 'cancel'] },
    { roles: TECH, actions: ['cancel'], ownOnly: true },
  ],
  ci: configRecord,
  asset: configRecord,
  article: [
    { roles: STAFF, actions: ['read', 'create', 'update'] },
    { roles: ADMIN, actions: ['publish', 'delete'] },
  ],
  catalog: [{ roles: STAFF, actions: ['read'] }],
  report: [{ roles: STAFF, actions: ['read'] }],
  company: adminManaged,
  contact: [
    { roles: STAFF, actions: ['read', 'create', 'update'] },
    { roles: ADMIN, actions: ['delete'] },
  ],
  contract: adminManaged,
  slaPolicy: adminManaged,
  assignmentRule: adminManaged,
  workOrder: [
    { roles: STAFF, actions: ['read', 'create'] },
    { roles: ADMIN, actions: ['update', 'assign', 'dispatch', 'close', 'cancel', 'delete'] },
    { roles: TECH, actions: ['update', 'close'], ownOnly: true },
  ],
  agent: [
    { roles: STAFF, actions: ['read', 'run'] },
    { roles: ADMIN, actions: ['create', 'update', 'delete', 'manage'] },
  ],
  agentRun: [{ roles: STAFF, actions: ['read'] }],
  user: [
    { roles: STAFF, actions: ['read'] },
    { roles: ADMIN, actions: ['create', 'update', 'manage'] },
    { roles: TECH, actions: ['update'], ownOnly: true },
  ],
  group: adminManaged,
  settings: [{ roles: ADMIN, actions: ['read', 'update'] }],
  auditLog: [
    { roles: ADMIN, actions: ['read'] },
    { roles: STAFF, actions: ['create'] },
  ],
  notification: [
    { roles: STAFF, actions: ['create'] },
    { roles: STAFF, actions: ['read', 'update'], ownOnly: true },
  ],
};

export function can(role: Role | null | undefined, action: Action, resource: Resource, ctx: PermissionContext = {}): boolean {
  if (!role) return false;
  const ownerMatches = ctx.uid != null && ctx.ownerId != null && ctx.uid === ctx.ownerId;
  return (MATRIX[resource] ?? []).some(
    (g) => g.roles.includes(role) && g.actions.includes(action) && (!g.ownOnly || ownerMatches),
  );
}
